import { cn } from "@/lib/utils";
import { ReactNode } from "react";
import { ProfileMenu } from "./ProfileMenu";
import TopNavBar, { TopNavItem } from "./TopNavBar";
import { useGetRolesQuery } from "@/hooks/queries";

// The Thomas Scott superapp portal. Clicking the logo / "← superapp" returns
// the user to it. Configurable per environment; defaults to production.
const SUPERAPP_URL =
  import.meta.env.VITE_PUBLIC_SUPERAPP_URL || "https://tsiltech.com";

const AppShell = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const { data: roleData } = useGetRolesQuery();
  const navBarItems: Array<TopNavItem> = roleData?.isAdmin
    ? [
        { label: "Home", icon: "home", isLink: true, href: "/" },
        { label: "Upload", isLink: true, icon: "uploadNavbar", href: "/upload" },
        { label: "Export", isLink: true, icon: "download", href: "/export" },
        {
          label: "Insights",
          isLink: true,
          icon: "insightsNavbar",
          href: "/insights",
        },
      ]
    : [
        {
          label: "Insights",
          isLink: true,
          icon: "insightsNavbar",
          href: "/insights",
        },
      ];

  return (
    <div className="flex h-screen w-full flex-col bg-[var(--color-bg)]">
      {/*------------------------Top Bar (mirrors TS-loom Topbar)--------------------*/}
      <header className="sticky top-0 z-30 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
        <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between gap-6 px-12">
          <div className="flex items-center gap-4">
            <a href={SUPERAPP_URL} className="flex items-center">
              <img
                src="/assets/ThomasScottHeaderLogo.png"
                alt="Thomas Scott"
                className="h-8 w-auto"
              />
            </a>
            <a
              href={SUPERAPP_URL}
              className="hidden items-center gap-1 font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--color-text)] opacity-70 transition-opacity hover:opacity-100 md:inline-flex"
            >
              ← superapp
            </a>
            <span className="hidden items-center gap-2 border border-[var(--color-border)] px-2 py-0.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--color-text)] md:inline-flex">
              <span className="block h-1.5 w-1.5 animate-dot-pulse bg-[var(--color-accent)]" />
              [ THREAD · v1 · ACTIVE ]
            </span>
          </div>

          <nav className="hidden items-center md:flex">
            <TopNavBar navBarItems={navBarItems} />
          </nav>

          <ProfileMenu />
        </div>
      </header>
      {/*------------------------Top Bar Ends--------------------*/}

      {/*------------------------Page Container--------------------*/}
      <div className="relative flex-1 overflow-auto bg-[var(--color-bg)]">
        {/* Same 1440px / px-12 gutters as the topbar so every page aligns with it */}
        <main className={cn("mx-auto w-full max-w-[1440px] px-12 py-8", className)}>
          {children}
        </main>
      </div>
      {/*------------------------Page Container Ends--------------------*/}
    </div>
  );
};

export default AppShell;
